"use client";

/**
 * STORY-NEW-043: Refatorado para usar componentes de layout padronizados
 */

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  useAddPro017ListItemsMutation,
  useGetPro017ListItemsQuery,
  useGetUnifiedCampaignByCodeQuery,
  useTogglePro017ListItemMutation,
  useUpdatePro017CampaignMutation,
} from "@/services/campaign";
import { useToast } from "components/ui/use-toast";
import { format } from "date-fns";
import { Ban, Check, Download, List, Loader2, Package, Plus, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExcelUploadPreview } from "../../create/components/shared/excel-upload-preview";
import { useExcelImport } from "../../create/components/shared/use-excel-import";
import { getTranslatedErrorMessage } from "../../utils/error-messages";
import { MESSAGES, NO_CHANGES_TOAST } from "../../utils/messages";
import {
  CampaignConflictModal,
  type CampaignConflictItem,
} from "../campaign-conflict-modal";
import {
  CampaignEditModalLayout,
  ModalErrorState,
  ModalFieldGroup,
  ModalInfoBox,
  ModalLoadingState,
} from "./modal-layout";
import { GlobalCampaignStoresBlock } from "./campaign-stores-editor";

interface EditPro017ModalProps {
  campaignCode: number;
}

interface ListData {
  listNumber: number;
  activeItemsCount: number;
  inactiveItemsCount: number;
  totalItemsCount: number;
}

export function EditPro017Modal({ campaignCode }: EditPro017ModalProps) {
  const { toast } = useToast();
  const { handleModal } = useDialogModal();

  const { data: campaign, isLoading, error, refetch } = useGetUnifiedCampaignByCodeQuery(
    `PRO017-${campaignCode}`
  );

  const [updateCampaign, { isLoading: isUpdating }] = useUpdatePro017CampaignMutation();

  const [addListItems] = useAddPro017ListItemsMutation();
  const [toggleItem] = useTogglePro017ListItemMutation();

  // Form state
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // List management state
  const [selectedList, setSelectedList] = useState<number | null>(null);
  const [newItemCode, setNewItemCode] = useState("");
  const [isAddingItem, setIsAddingItem] = useState(false);

  const lists: ListData[] = campaign?.lists || [];

  // Load campaign data
  useEffect(() => {
    if (campaign) {
      setDescription(campaign.description || "");
      setStartDate(campaign.startDate ? format(new Date(campaign.startDate), "yyyy-MM-dd") : "");
      setEndDate(campaign.endDate ? format(new Date(campaign.endDate), "yyyy-MM-dd") : "");
    }
  }, [campaign]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!description || description.trim().length < 3) {
      newErrors.description = "Descrição deve ter no mínimo 3 caracteres";
    }
    if (description.length > 100) {
      newErrors.description = "Descrição deve ter no máximo 100 caracteres";
    }
    if (!startDate) {
      newErrors.startDate = "Data de início é obrigatória";
    }
    if (!endDate) {
      newErrors.endDate = "Data de término é obrigatória";
    }
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      newErrors.endDate = "Data de término deve ser maior ou igual à data de início";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const originalStartDate = campaign?.startDate
        ? format(new Date(campaign.startDate), "yyyy-MM-dd")
        : "";
      const originalEndDate = campaign?.endDate
        ? format(new Date(campaign.endDate), "yyyy-MM-dd")
        : "";

      const updateData: {
        code: number;
        description?: string;
        startDate?: string;
        endDate?: string;
      } = { code: campaignCode };

      if (description !== (campaign?.description ?? "")) updateData.description = description;
      if (startDate !== originalStartDate) updateData.startDate = startDate;
      if (endDate !== originalEndDate) updateData.endDate = endDate;

      const changedFields = Object.keys(updateData).filter((k) => k !== "code");
      if (changedFields.length === 0) {
        toast(NO_CHANGES_TOAST);
        return;
      }

      await updateCampaign(updateData).unwrap();

      toast(MESSAGES.UPDATE.SUCCESS);

      handleModal({ isOpen: false, element: null, title: "", description: "" });
    } catch (err: unknown) {
      const typedErr = err as { status?: number; data?: { message?: string; messages?: string[]; conflicts?: CampaignConflictItem[] } };

      if (typedErr?.status === 409 && typedErr?.data?.conflicts) {
        setConflicts(typedErr.data.conflicts);
        setShowConflictModal(true);
        toast(MESSAGES.CONFLICT.ERROR);
        return;
      }

      const errorMessage = getTranslatedErrorMessage(
        typedErr,
        MESSAGES.UPDATE.ERROR.description,
      );

      toast({ ...MESSAGES.UPDATE.ERROR, description: errorMessage });
    }
  };

  const handleCancel = () => {
    handleModal({ isOpen: false, element: null, title: "", description: "" });
  };

  const handleCloseConflictModal = () => {
    setShowConflictModal(false);
    setConflicts([]);
  };

  const handleAddItem = async (listNumber: number) => {
    if (!newItemCode.trim()) return;

    setIsAddingItem(true);
    try {
      await addListItems({
        code: campaignCode,
        listNumber,
        itemCodes: [newItemCode.trim()],
      }).unwrap();

      toast({
        ...MESSAGES.ADD_ITEMS.SUCCESS,
        description: `Item ${newItemCode} adicionado à lista ${listNumber}.`,
      });

      setNewItemCode("");
      refetch();
    } catch (err: unknown) {
      const errorMessage = getTranslatedErrorMessage(
        err as { status?: number; data?: { message?: string; messages?: string[] } },
        MESSAGES.ADD_ITEMS.ERROR.description,
      );
      toast({ ...MESSAGES.ADD_ITEMS.ERROR, description: errorMessage });
    } finally {
      setIsAddingItem(false);
    }
  };

  // Story 1.5: Bulk add items from Excel import
  const handleBulkAddItems = async (listNumber: number, itemCodes: string[]): Promise<void> => {
    if (itemCodes.length === 0) return;

    try {
      await addListItems({
        code: campaignCode,
        listNumber,
        itemCodes,
      }).unwrap();

      toast({
        ...MESSAGES.ADD_ITEMS.SUCCESS,
        description: `${itemCodes.length} item(ns) adicionado(s) à lista ${listNumber}.`,
      });
    } catch (err: unknown) {
      const typedErr = err as { status?: number; data?: { message?: string; messages?: string[]; conflicts?: CampaignConflictItem[] } };

      if (typedErr?.status === 409 && typedErr?.data?.conflicts) {
        setConflicts(typedErr.data.conflicts);
        setShowConflictModal(true);
        toast(MESSAGES.CONFLICT.ERROR);
        return;
      }

      const errorMessage = getTranslatedErrorMessage(
        typedErr,
        MESSAGES.ADD_ITEMS.ERROR.description,
      );
      toast({ ...MESSAGES.ADD_ITEMS.ERROR, description: errorMessage });
    }
  };

  const handleToggleItem = async (listNumber: number, itemCode: string, isCurrentlyActive: boolean) => {
    try {
      const nextActive = !isCurrentlyActive;
      const normalizedItemCode = String(itemCode ?? "").trim();
      if (!normalizedItemCode) {
        toast(MESSAGES.INVALID_ITEM.ERROR);
        return;
      }
      await toggleItem({
        code: campaignCode,
        listNumber,
        itemCode: normalizedItemCode,
        active: nextActive,
      }).unwrap();
      toast({
        ...MESSAGES.TOGGLE_ITEM.SUCCESS,
        description: `Item ${normalizedItemCode} ${nextActive ? "ativado" : "desativado"}.`,
      });
      refetch();
    } catch (err: unknown) {
      const errorMessage = getTranslatedErrorMessage(
        err as { status?: number; data?: { message?: string; messages?: string[] } },
        MESSAGES.TOGGLE_ITEM.ERROR.description,
      );
      toast({ ...MESSAGES.TOGGLE_ITEM.ERROR, description: errorMessage });
    }
  };

  const visualStatus = (campaign?.visualStatus ?? campaign?.status ?? "FUTURE") as string;
  const isFuture = visualStatus === "FUTURE";
  const isActive = visualStatus === "ACTIVE";
  const isExpired = visualStatus === "EXPIRED";

  // Loading state
  if (isLoading) {
    return <ModalLoadingState />;
  }

  // Error state
  if (error || !campaign) {
    const loadErrorMessage = getTranslatedErrorMessage(
      error as { status?: number; data?: { message?: string; messages?: string[] } },
      "Erro ao carregar campanha. Tente novamente."
    );
    return <ModalErrorState details={loadErrorMessage} />;
  }

  return (
    <CampaignEditModalLayout
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isUpdating}
    >
      {/* Descrição */}
      <div className="space-y-2">
        <Label htmlFor="description">Descrição *</Label>
        <Input
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descrição da campanha"
          maxLength={100}
        />
        <p className="text-xs text-muted-foreground">{description.length}/100 caracteres</p>
        {errors.description && (
          <p className="text-sm text-destructive">{errors.description}</p>
        )}
      </div>

      {/* Datas — startDate desabilitado em ACTIVE/EXPIRED */}
      <ModalFieldGroup columns={2}>
        <div className="space-y-2">
          <Label htmlFor="startDate">Data de Início *</Label>
          <Input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={!isFuture}
            title={!isFuture ? "Data de início não pode ser alterada em campanhas ativas/expiradas" : undefined}
          />
          {!isFuture && (
            <p className="text-xs text-muted-foreground">
              Não é possível alterar a data de início em campanhas {isActive ? "ativas" : "expiradas"}.
            </p>
          )}
          {errors.startDate && (
            <p className="text-sm text-destructive">{errors.startDate}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="endDate">Data de Término *</Label>
          <Input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={campaign?.startDate ? format(new Date(campaign.startDate), "yyyy-MM-dd") : undefined}
            disabled={isExpired}
          />
          {errors.endDate && (
            <p className="text-sm text-destructive">{errors.endDate}</p>
          )}
        </div>
      </ModalFieldGroup>

      {/* Resumo da campanha */}
      <ModalInfoBox>
        <p className="text-sm font-medium mb-2">Resumo da Campanha</p>
        <ModalFieldGroup columns={2}>
          <div>
            <span className="text-xs text-muted-foreground">Tipo de Desconto:</span>{" "}
            <span className="text-xs font-medium">
              {campaign.discountType === 0 ? "Valor Fixo (R$)" : "Percentual (%)"}
            </span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Valor:</span>{" "}
            <span className="text-xs font-medium">
              {campaign.discountType === 0
                ? `R$ ${campaign.discountValue?.toFixed(2)}`
                : `${campaign.discountValue}%`
              }
            </span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Listas:</span>{" "}
            <span className="text-xs font-medium">{lists.length || 0}</span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Total de Itens:</span>{" "}
            <span className="text-xs font-medium">
              {lists.reduce((acc, l) => acc + (l.totalItemsCount || 0), 0)}
            </span>
          </div>
        </ModalFieldGroup>
      </ModalInfoBox>

      {/* Listas e Itens */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <List className="h-4 w-4" />
          <Label>Listas do Combo</Label>
        </div>

        {lists.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma lista cadastrada</p>
        ) : (
          <Accordion type="single" collapsible className="w-full">
            {lists.map((list) => (
              <ListAccordionItem
                key={list.listNumber}
                campaignCode={campaignCode}
                list={list}
                onAddItem={handleAddItem}
                onToggleItem={handleToggleItem}
                onBulkAddItems={handleBulkAddItems}
                newItemCode={selectedList === list.listNumber ? newItemCode : ""}
                setNewItemCode={(value) => {
                  setSelectedList(list.listNumber);
                  setNewItemCode(value);
                }}
                isAddingItem={isAddingItem && selectedList === list.listNumber}
                isReadOnly={campaign.visualStatus === "EXPIRED"}
              />
            ))}
          </Accordion>
        )}
      </div>

      {/* Lojas — PRO017 é sempre global (sem dimensão de loja): edição bloqueada */}
      <GlobalCampaignStoresBlock />

      <CampaignConflictModal
        isOpen={showConflictModal}
        conflicts={conflicts}
        onClose={handleCloseConflictModal}
        onGoBack={handleCloseConflictModal}
      />
    </CampaignEditModalLayout>
  );
}

// Sub-component for each list
interface ListAccordionItemProps {
  campaignCode: number;
  list: ListData;
  onAddItem: (listNumber: number) => Promise<void>;
  onToggleItem: (listNumber: number, itemCode: string, isActive: boolean) => Promise<void>;
  onBulkAddItems: (listNumber: number, itemCodes: string[]) => Promise<void>;
  newItemCode: string;
  setNewItemCode: (value: string) => void;
  isAddingItem: boolean;
  isReadOnly: boolean;
}

function ListAccordionItem({
  campaignCode,
  list,
  onAddItem,
  onToggleItem,
  onBulkAddItems,
  newItemCode,
  setNewItemCode,
  isAddingItem,
  isReadOnly,
}: ListAccordionItemProps) {
  const { data: listItems, isLoading: isLoadingItems } = useGetPro017ListItemsQuery({
    code: campaignCode,
    listNumber: list.listNumber,
  });

  const items = listItems?.items || [];

  // Story 1.5: Excel import for this list.
  // NOTA: inclui ativos E inativos na detecção de duplicado — se o SKU já está
  // na tabela (mesmo desativado), o backend retorna itemsAdded=0 silenciosamente.
  // Filtrar só por isActive esconderia essas colisões do preview e produziria
  // toast de sucesso falso. Ver bug report 2026-04-22.
  const currentListAsSkuItems = useMemo(
    () => items.map((i) => ({ sku: i.code })),
    [items],
  );

  const {
    fileInputRef,
    uploadProgress,
    isProcessing,
    preview,
    handleFileUpload,
    closePreview,
    confirmImport,
  } = useExcelImport(currentListAsSkuItems);

  const handleConfirmImport = useCallback(
    async (importedItems: Array<{ sku: string; name?: string }>): Promise<void> => {
      await confirmImport(importedItems, async (confirmed) => {
        const skus = confirmed.map((item) => item.sku);
        await onBulkAddItems(list.listNumber, skus);
      });
    },
    [confirmImport, onBulkAddItems, list.listNumber],
  );

  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleTriggerClick = useCallback(() => {
    setTimeout(() => {
      triggerRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }, 250);
  }, []);

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/templates/sku-import-template.xlsx";
    link.download = "sku-import-template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AccordionItem value={`list-${list.listNumber}`} className="w-full min-w-0">
      <AccordionTrigger
        ref={triggerRef}
        onClick={handleTriggerClick}
        className="hover:no-underline"
      >
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4" />
          <span>Lista {list.listNumber}</span>
          <Badge variant="secondary" className="ml-2">
            {list.activeItemsCount} ativos / {list.totalItemsCount} total
          </Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent className="w-full max-w-full overflow-hidden">
        <div className="space-y-3 pt-2">
          {/* Add item input */}
          <div className="flex gap-2">
            <Input
              placeholder="Código do item (SKU)"
              value={newItemCode}
              onChange={(e) => setNewItemCode(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && onAddItem(list.listNumber)}
              disabled={isReadOnly}
            />
            <Button
              size="sm"
              onClick={() => onAddItem(list.listNumber)}
              disabled={isAddingItem || !newItemCode.trim() || isReadOnly}
            >
              {isAddingItem ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Story 1.5: Excel import buttons */}
          {!isReadOnly && (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                className="flex-1"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Template
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                className="flex-1"
              >
                {isProcessing ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                )}
                {uploadProgress !== null ? `${uploadProgress}%` : "Importar Excel"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          )}
          {uploadProgress !== null && (
            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-primary h-full transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {/* Items list */}
          {isLoadingItems ? (
            <div className="flex justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum item nesta lista</p>
          ) : (
            <div className="max-h-72 w-full max-w-full overflow-y-auto overflow-x-hidden rounded-md border p-2">
              <div className="flex flex-wrap gap-2 min-w-0">
                {items.map((item) => (
                  <Badge
                    key={item.code}
                    variant={item.isActive ? "default" : "outline"}
                    className="cursor-pointer flex items-center gap-1 shrink-0"
                    onClick={() => onToggleItem(list.listNumber, item.code, item.isActive)}
                  >
                    {item.isActive ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <Ban className="h-3 w-3" />
                    )}
                    {item.code}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Clique em um item para ativar/desativar
          </p>
        </div>

        {preview && (
          <ExcelUploadPreview
            isOpen={!!preview}
            onClose={closePreview}
            onConfirm={handleConfirmImport}
            validItems={preview.validItems}
            invalidSkus={preview.invalidSkus}
            duplicateSkus={preview.duplicateSkus}
          />
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
